import { FC, useEffect, useState } from 'react'
import type { Swiper as SwiperClass } from 'swiper'
import styled from 'styled-components'
import { Box } from '@/components/atoms/Grid'
import { BreakpointKey } from '@/theme/breakpoints'
import ProductsSlider, {
  ProductSlide,
  SliderDirection
} from '../productsSlider'
import { PictureWithPlaceholder } from '@/components/atoms/picture'
import { PlaceholderColor } from '@/components/atoms/productCardPlaceholder'
import { getProductImageUrls, ProductImageSize } from '@/utils/productImage'
import { useProductImage } from 'hooks/useProductImage'
import { ProductImageArea } from '@/core/productImageReporter'

export enum LazyMode {
  All,
  AllExceptFirst,
  None
}

type ProductDetailImageBrowserProps = {
  showBullets: boolean
  images: string[]
  breakpoint: BreakpointKey
  imageSizeBreakpoint: BreakpointKey
  onImageClick?: (offset: number) => void
  preload?: boolean
  ratio: number
  lazy: LazyMode
  imageArea: ProductImageArea
  useThumbor: boolean
} & Rest

const getImageSize = (imageSizeBreakpoint: BreakpointKey): ProductImageSize => {
  switch (imageSizeBreakpoint) {
    case BreakpointKey.mobile:
      return { width: 800, height: 1200 }
    case BreakpointKey.tablet:
      return { width: 800, height: 800 }
    default:
      return { width: 1400, height: 1400 }
  }
}

type GalleryImageProps = {
  url: string
  breakpoint: BreakpointKey
  imageSizeBreakpoint: BreakpointKey
  imageArea: ProductImageArea
  useThumbor: boolean
  ratio: number
  lazy: boolean
  preload: boolean
  onClick: () => void
}

const GalleryImage: FC<GalleryImageProps> = ({
  url,
  breakpoint,
  imageSizeBreakpoint,
  imageArea,
  useThumbor,
  ratio,
  lazy,
  preload,
  onClick
}) => {
  const image = useProductImage(
    getProductImageUrls(url, getImageSize(imageSizeBreakpoint), {
      fill: imageSizeBreakpoint !== BreakpointKey.mobile,
      useThumbor
    }),
    { area: imageArea, measureSlow: !lazy }
  )
  return (
    <PictureWithPlaceholder
      // this will load transparent gif pixel for tablet/mobile
      src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
      desktop={breakpoint === BreakpointKey.desktop ? image.src : null}
      tablet={breakpoint === BreakpointKey.tablet ? image.src : null}
      mobile={breakpoint === BreakpointKey.mobile ? image.src : null}
      preload={preload}
      preloadDefaultMedia={preload && breakpoint}
      onClick={onClick}
      lazy={lazy}
      ratio={ratio}
      onImageLoad={image.onLoad}
      onImageError={image.onError}
      imageRef={image.ref}
    />
  )
}

const ProductDetailImageBrowser: FC<ProductDetailImageBrowserProps> = ({
  showBullets,
  images,
  breakpoint,
  imageSizeBreakpoint,
  onImageClick,
  preload = true,
  ratio,
  lazy,
  imageArea,
  useThumbor,
  ...rest
}) => {
  const [loadedSlides, setLoadedSlides] = useState(1)

  const loadSlidesUpTo = (count: number) =>
    setLoadedSlides((current) => Math.max(current, count))

  useEffect(() => {
    const loadNextSlide = () => loadSlidesUpTo(2)
    if (document.readyState === 'complete') {
      loadNextSlide()
      return
    }
    window.addEventListener('load', loadNextSlide)
    return () => window.removeEventListener('load', loadNextSlide)
  }, [])

  const onSwiper = (swiper: SwiperClass) => {
    swiper.on('activeIndexChange', ({ activeIndex }) =>
      loadSlidesUpTo(activeIndex + 2)
    )
  }

  return (
    <ProductsSlider
      showPagination={showBullets}
      slidesToShow={1}
      slidesPerGroup={1}
      sliderDirection={SliderDirection.horizontal}
      spaceBetween={0}
      onSwiper={onSwiper}
      {...rest}
    >
      {images &&
        images.map((url, offset) => {
          return (
            <ProductSlide key={offset}>
              <Box mb={showBullets ? 20 : 0}>
                {offset < loadedSlides ? (
                  <GalleryImage
                    url={url}
                    breakpoint={breakpoint}
                    imageSizeBreakpoint={imageSizeBreakpoint}
                    imageArea={imageArea}
                    useThumbor={useThumbor}
                    ratio={ratio}
                    preload={preload && offset === 0}
                    onClick={() => onImageClick && onImageClick(offset)}
                    lazy={
                      lazy === LazyMode.All
                        ? true
                        : lazy === LazyMode.AllExceptFirst
                        ? offset !== 0
                        : false
                    }
                  />
                ) : (
                  <SlidePlaceholder pb={`${ratio * 100}%`} />
                )}
              </Box>
            </ProductSlide>
          )
        })}
    </ProductsSlider>
  )
}

export default ProductDetailImageBrowser

const SlidePlaceholder = styled(Box)`
  ${PlaceholderColor}
`
